import { useState, useRef } from 'react';
import { auth } from '../../lib/firebase.js';
import { ApiError } from '../../lib/api-client.js';
import { Button } from '../../components/Button.js';
import { MAX_PHOTOS_PER_UPLOAD } from '@ridehub/shared';

interface PhotoUploadProps {
  spotId: string;
  onUploadSuccess?: (photos: { id: string; url: string; userId: string }[]) => void;
}

export function PhotoUpload({ spotId, onUploadSuccess }: PhotoUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doUpload = async (formData: FormData, forceRefresh: boolean): Promise<Response> => {
    const user = auth.currentUser;
    if (!user) throw new ApiError(401, 'AUTHENTICATION_ERROR', '認証が必要です');
    const token = await user.getIdToken(forceRefresh);
    return fetch(`/api/spots/${spotId}/photos`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
  };

  const handleUpload = async (files: FileList) => {
    const selected = Array.from(files).slice(0, MAX_PHOTOS_PER_UPLOAD);
    setUploading(true);
    setError(null);

    try {
      if (!auth.currentUser) throw new ApiError(401, 'AUTHENTICATION_ERROR', '認証が必要です');

      const formData = new FormData();
      selected.forEach((file) => formData.append('photos', file));

      let response = await doUpload(formData, false);

      // トークン期限切れの場合、強制リフレッシュして1度だけリトライ
      if (response.status === 401 && auth.currentUser) {
        response = await doUpload(formData, true);
      }

      if (!response.ok) {
        const data = await response.json();
        throw new ApiError(
          response.status,
          data.error?.code ?? 'UPLOAD_ERROR',
          data.error?.message ?? 'アップロードに失敗しました'
        );
      }

      const data = await response.json();
      onUploadSuccess?.(data.data.photos);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => e.target.files && handleUpload(e.target.files)}
      />
      <Button
        variant="secondary"
        size="sm"
        loading={uploading}
        onClick={() => fileInputRef.current?.click()}
      >
        📷 写真を追加 (最大{MAX_PHOTOS_PER_UPLOAD}枚)
      </Button>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
}
